import { Component, OnInit, QueryList, ViewChildren, inject, input } from '@angular/core';
import { CallForward, SelectFieldConfig, TextFieldConfig } from '../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroSelectConfig } from '../../../../forms/fields/select/ziro-select.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import {
  CallForwardField,
  ZiroCallForwardComponent,
  ZiroCallForwardConfig,
} from '../../call-forward/call-forward.component';
import { Observable, of } from 'rxjs';
import { ZiroFormStateService } from '../../../../forms/ziro-form-state.service';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash';

export interface DnCallForwardForm {
  secondaryClassOfServiceForForwardAll: string;
  noAnswerRingDuration: string;
  forwardAll: CallForward;
  forwardBusyInternal: CallForward;
  forwardBusyExternal: CallForward;
  forwardNoAnswerInternal: CallForward;
  forwardNoAnswerExternal: CallForward;
  forwardNoCoverageInternal: CallForward;
  forwardNoCoverageExternal: CallForward;
  forwardUnregisteredInternal: CallForward;
  forwardUnregisteredExternal: CallForward;
  forwardOnCtiFailure: CallForward;
}

export interface DnCallForwardFormData {
  forwardAllCss: string;
  secondaryClassOfServiceForForwardAll: string;
  noAnswerRingDuration: string;
  forwardAll: CallForwardField;
  forwardBusyExternal: CallForwardField;
  forwardNoAnswerExternal: CallForwardField;
}

export interface DnCallForwardFieldConfig {
  forwardAllCss: SelectFieldConfig<string>;
  secondaryClassOfServiceForForwardAll: SelectFieldConfig<string>;
  noAnswerRingDuration: TextFieldConfig;
}

@Component({
  selector: 'ziro-directory-number-call-forward-form',
  templateUrl: './directory-number-call-forward-form.component.html',
  standalone: false,
})
export class DirectoryNumberCallForwardFormComponent
  extends ZiroFormAbstractDirective<DnCallForwardForm, DnCallForwardFormData>
  implements OnInit
{
  protected _ziroFormStateService: ZiroFormStateService;
  private translateService = inject(TranslateService);

  @ViewChildren(ZiroCallForwardComponent) callForwardComponents: QueryList<ZiroCallForwardComponent>;
  readonly fieldConfig = input<DnCallForwardFieldConfig>();
  readonly hasVoicemail = input<boolean>();
  readonly isUnityServerConfigured = input<boolean>();
  readonly hideWarning = input<boolean>();

  formConfig: ZiroFormConfig;

  private initialEntity: DnCallForwardForm;

  ngOnInit(): void {
    this.initialEntity = cloneDeep(this.entity);

    this.formConfig = {
      fields: {
        forwardAllCss: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.classOfService.label',
          dataAutomation: 'dn-call-forward-class-of-service',
          componentConfig: new ZiroSelectConfig({ options: this.fieldConfig().forwardAllCss.possibleOptions }),
          defaultValue: () => this.fieldConfig().forwardAllCss.defaultValue,
          hidden: () => !this.fieldConfig().forwardAllCss.show,
          required: () => this.fieldConfig().forwardAllCss.required,
        },
        secondaryClassOfServiceForForwardAll: {
          label: 'tkey;shared.dnWizard.dnDetails.dnCallForward.secondaryClassOfService.label',
          dataAutomation: 'dn-call-forward-secondary-class-of-service',
          componentConfig: new ZiroSelectConfig({
            options: this.fieldConfig().secondaryClassOfServiceForForwardAll.possibleOptions,
          }),
          defaultValue: () => this.fieldConfig().secondaryClassOfServiceForForwardAll.defaultValue,
          hidden: () => !this.fieldConfig().secondaryClassOfServiceForForwardAll.show,
          required: () => this.fieldConfig().secondaryClassOfServiceForForwardAll.required,
        },
        noAnswerRingDuration: {
          label: 'tkey;shared.dnWizard.dnDetails.dnExtensionDetails.no_answer_ring_duration.label',
          dataAutomation: 'dn-call-forward-no-answer-ring-duration',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.NUMBER }),
          defaultValue: () => this.fieldConfig().noAnswerRingDuration.defaultValue,
          hidden: () => !this.fieldConfig().noAnswerRingDuration.show,
          required: () => this.fieldConfig().noAnswerRingDuration.required,
          validation: [
            {
              validator: (val: number) =>
                !val || val >= 1 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: this.translateService.instant('tkey;validators.global.error.min', {
                minValue: 1,
              }),
            },
            {
              validator: (val: number) =>
                !val || val <= 300 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: this.translateService.instant('tkey;validators.global.error.max', {
                maxValue: 300,
              }),
            },
          ],
        },
        forwardAll: {
          dataAutomation: 'call-forward-all-select',
          required: false,
          label: 'tkey;shared.dnWizard.dnDetails.dnCallForward.all.label',
          componentConfig: new ZiroCallForwardConfig({
            hasVoicemail: this.hasVoicemail(),
            isUnityServerConfigured: this.isUnityServerConfigured(),
            hideWarning: this.hideWarning(),
          }),
        },
        forwardBusyExternal: {
          dataAutomation: 'call-forward-busy-select',
          required: false,
          label: 'tkey;shared.dnWizard.dnDetails.dnCallForward.destinationBusy.label',
          componentConfig: new ZiroCallForwardConfig({
            hasVoicemail: this.hasVoicemail(),
            isUnityServerConfigured: this.isUnityServerConfigured(),
            hideWarning: this.hideWarning(),
          }),
        },
        forwardNoAnswerExternal: {
          dataAutomation: 'call-forward-no-answer-select',
          required: false,
          label: 'tkey;shared.dnWizard.dnDetails.dnCallForward.noAnswer.label',
          componentConfig: new ZiroCallForwardConfig({
            hasVoicemail: this.hasVoicemail(),
            isUnityServerConfigured: this.isUnityServerConfigured(),
            hideWarning: this.hideWarning(),
          }),
        },
      },
    };
  }

  toFormData = (entity: DnCallForwardForm): DnCallForwardFormData => {
    return {
      forwardAllCss: entity.forwardAll.classOfService,
      secondaryClassOfServiceForForwardAll: entity.secondaryClassOfServiceForForwardAll,
      noAnswerRingDuration: entity.noAnswerRingDuration,
      forwardAll: {
        destination: entity.forwardAll.destination,
        forwardToVoicemail: entity.forwardAll.forwardToVoicemail,
      },
      forwardBusyExternal: {
        destination: entity.forwardBusyExternal.destination,
        forwardToVoicemail: entity.forwardBusyExternal.forwardToVoicemail,
      },
      forwardNoAnswerExternal: {
        destination: entity.forwardNoAnswerExternal.destination,
        forwardToVoicemail: entity.forwardNoAnswerExternal.forwardToVoicemail,
      },
    };
  };

  toEntity = (formData: DnCallForwardFormData): DnCallForwardForm => {
    // Values set in CUCM are expected to be preserved until a value set in our UI matches the one set in CUCM.
    // At that point the settings become synced with our UI. Updates go as follows:
    // 1. Class Of Service (Calling Search Space) for Forward All controls all other classOfService's EXCEPT secondaryClassOfServiceForForwardAll which only controls itself
    // 2. Destination
    //    a) Forward All - Controls itself
    //    b) Forward Busy External - Controls itself AND Forward Busy Internal
    //    c) Forward No Answer External - Controls itself and all the rest

    const newEntity = cloneDeep(this.entity);

    newEntity.noAnswerRingDuration = formData.noAnswerRingDuration;
    newEntity.secondaryClassOfServiceForForwardAll =
      !this.initialEntity.secondaryClassOfServiceForForwardAll ||
      (!!this.initialEntity.secondaryClassOfServiceForForwardAll &&
        this.initialEntity.secondaryClassOfServiceForForwardAll !== formData.secondaryClassOfServiceForForwardAll)
        ? formData.secondaryClassOfServiceForForwardAll
        : this.initialEntity.secondaryClassOfServiceForForwardAll;
    newEntity.forwardAll = {
      classOfService: formData.forwardAllCss,
      destination: formData.forwardAll?.destination,
      forwardToVoicemail: formData.forwardAll?.forwardToVoicemail,
    };

    // Busy
    newEntity.forwardBusyExternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardBusyExternal.classOfService ||
        !this.initialEntity.forwardBusyExternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardBusyExternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardBusyExternal.classOfService,
      destination: formData.forwardBusyExternal?.destination,
      forwardToVoicemail: formData.forwardBusyExternal?.forwardToVoicemail,
    };

    newEntity.forwardBusyInternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardBusyInternal.classOfService ||
        !this.initialEntity.forwardBusyInternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardBusyInternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardBusyInternal.classOfService,
      destination:
        formData.forwardBusyExternal?.destination === this.initialEntity.forwardBusyInternal.destination ||
        !this.initialEntity.forwardBusyInternal.destination ||
        this.initialEntity.forwardBusyExternal.destination === this.initialEntity.forwardBusyInternal.destination
          ? formData.forwardBusyExternal?.destination
          : this.initialEntity.forwardBusyInternal.destination,
      forwardToVoicemail: formData.forwardBusyExternal?.forwardToVoicemail,
    };

    // No Answer
    newEntity.forwardNoAnswerExternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardNoAnswerExternal.classOfService ||
        !this.initialEntity.forwardNoAnswerExternal.classOfService ||
        this.initialEntity.forwardAll.classOfService == this.initialEntity.forwardNoAnswerExternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardNoAnswerExternal.classOfService,
      destination: formData.forwardNoAnswerExternal?.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardNoAnswerInternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardNoAnswerInternal.classOfService ||
        !this.initialEntity.forwardNoAnswerInternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardNoAnswerInternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardNoAnswerInternal.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardNoAnswerInternal.destination ||
        !this.initialEntity.forwardNoAnswerInternal.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination ===
          this.initialEntity.forwardNoAnswerInternal.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardNoAnswerInternal.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardNoCoverageInternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardNoCoverageInternal.classOfService ||
        !this.initialEntity.forwardNoCoverageInternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardNoCoverageInternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardNoCoverageInternal.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardNoCoverageInternal.destination ||
        !this.initialEntity.forwardNoCoverageInternal.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination ===
          this.initialEntity.forwardNoCoverageInternal.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardNoCoverageInternal.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardNoCoverageExternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardNoCoverageExternal.classOfService ||
        !this.initialEntity.forwardNoCoverageExternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardNoCoverageExternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardNoCoverageExternal.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardNoCoverageExternal.destination ||
        !this.initialEntity.forwardNoCoverageExternal.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination ===
          this.initialEntity.forwardNoCoverageExternal.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardNoCoverageExternal.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardUnregisteredInternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardUnregisteredInternal.classOfService ||
        !this.initialEntity.forwardUnregisteredInternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardUnregisteredInternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardUnregisteredInternal.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardUnregisteredInternal.destination ||
        !this.initialEntity.forwardUnregisteredInternal.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination ===
          this.initialEntity.forwardUnregisteredInternal.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardUnregisteredInternal.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardUnregisteredExternal = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardUnregisteredExternal.classOfService ||
        !this.initialEntity.forwardUnregisteredExternal.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardUnregisteredExternal.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardUnregisteredExternal.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardUnregisteredExternal.destination ||
        !this.initialEntity.forwardUnregisteredExternal.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination ===
          this.initialEntity.forwardUnregisteredExternal.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardUnregisteredExternal.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };

    newEntity.forwardOnCtiFailure = {
      classOfService:
        formData.forwardAllCss === this.initialEntity.forwardOnCtiFailure.classOfService ||
        !this.initialEntity.forwardOnCtiFailure.classOfService ||
        this.initialEntity.forwardAll.classOfService === this.initialEntity.forwardOnCtiFailure.classOfService
          ? formData.forwardAllCss
          : this.initialEntity.forwardOnCtiFailure.classOfService,
      destination:
        formData.forwardNoAnswerExternal?.destination === this.initialEntity.forwardOnCtiFailure.destination ||
        !this.initialEntity.forwardOnCtiFailure.destination ||
        this.initialEntity.forwardNoAnswerExternal.destination === this.initialEntity.forwardOnCtiFailure.destination
          ? formData.forwardNoAnswerExternal?.destination
          : this.initialEntity.forwardOnCtiFailure.destination,
      forwardToVoicemail: formData.forwardNoAnswerExternal?.forwardToVoicemail,
    };
    return newEntity;
  };

  getAreChildrenValid(): boolean {
    return !this.callForwardComponents.find(
      (child) => child.showErrorMessage() && child.validationState === ZiroFormsValidationState.VALID,
    );
  }

  protected submit(): Observable<null> {
    return of(null);
  }
}
